import {
	CzechSuklRsAtcGroupAutocompleteItemResponse,
	CzechSuklRsAtcGroupResponse,
	ErrorsResponse,
	ItemsResponse,
	PaginatedItemsResponse,
	WrappedExportResponse,
} from 'com.cogvio.pricemonitor.api';
import type {
	QueryFunction,
	QueryKey,
	UseMutationOptions,
	UseQueryOptions,
	UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ErrorType, apiClientMutator } from '~/infrastructure/config/mutator';

export interface CzechSuklRsAtcGroupsParams {
	page?: number;
	perPage?: number;
	ids?: string[];
	rs?: string[];
}

export interface CzechSuklRsAtcGroupsAutocompleteParams {
	filter?: string;
	page?: number;
	perPage?: number;
}

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (config: any, args: infer P) => any ? P : never;

export function getCzechSuklRsAtcGroups(
	params?: CzechSuklRsAtcGroupsParams,
	options?: SecondParameter<typeof apiClientMutator>
) {
	return apiClientMutator<PaginatedItemsResponse<CzechSuklRsAtcGroupResponse>>(
		{
			url: '/product-sukl-rs-atc-groups',
			method: 'get',
			params,
		},
		options
	);
}

export function getCzechSuklRsAtcGroupsQueryKey(params?: CzechSuklRsAtcGroupsParams) {
	return ['/product-sukl-rs-atc-groups', params] as const;
}

type GetCzechSuklRsAtcGroupsQueryReturnType = Awaited<ReturnType<typeof getCzechSuklRsAtcGroups>>;

export type GetCzechSuklRsAtcGroupsQueryResult = NonNullable<GetCzechSuklRsAtcGroupsQueryReturnType>;

export type GetCzechSuklRsAtcGroupsQueryError = ErrorType<ErrorsResponse>;

export function useCzechSuklRsAtcGroups<
	TData = GetCzechSuklRsAtcGroupsQueryReturnType,
	TError = GetCzechSuklRsAtcGroupsQueryError
>(
	params?: CzechSuklRsAtcGroupsParams,
	options?: {
		query?: UseQueryOptions<GetCzechSuklRsAtcGroupsQueryReturnType, TError, TData>;
		request?: SecondParameter<typeof apiClientMutator>;
	}
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getCzechSuklRsAtcGroupsQueryKey(params);

	const queryFn: QueryFunction<GetCzechSuklRsAtcGroupsQueryReturnType> = () =>
		getCzechSuklRsAtcGroups(params, requestOptions);

	const query = useQuery<GetCzechSuklRsAtcGroupsQueryReturnType, TError, TData>(queryKey, queryFn, {
		keepPreviousData: true,
		...queryOptions,
	}) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

	query.queryKey = queryKey;

	return query;
}

export function getCzechSuklRsAtcGroupsAutocomplete(
	params?: CzechSuklRsAtcGroupsAutocompleteParams,
	options?: SecondParameter<typeof apiClientMutator>
) {
	return apiClientMutator<PaginatedItemsResponse<CzechSuklRsAtcGroupAutocompleteItemResponse>>(
		{
			url: '/product-sukl-rs-atc-groups/autocomplete',
			method: 'get',
			params,
		},
		options
	);
}

export function getCzechSuklRsAtcGroupsAutocompleteQueryKey(params?: CzechSuklRsAtcGroupsAutocompleteParams) {
	return ['/product-sukl-rs-atc-groups/autocomplete', params] as const;
}

type GetCzechSuklRsAtcGroupsAutocompleteQueryReturnType = Awaited<
	ReturnType<typeof getCzechSuklRsAtcGroupsAutocomplete>
>;

export type GetCzechSuklRsAtcGroupsAutocompleteQueryResult =
	NonNullable<GetCzechSuklRsAtcGroupsAutocompleteQueryReturnType>;

export type GetCzechSuklRsAtcGroupsAutocompleteQueryError = ErrorType<ErrorsResponse>;

export function useCzechSuklRsAtcGroupsAutocomplete<
	TData = GetCzechSuklRsAtcGroupsAutocompleteQueryReturnType,
	TError = GetCzechSuklRsAtcGroupsAutocompleteQueryError
>(
	params?: CzechSuklRsAtcGroupsAutocompleteParams,
	options?: {
		query?: UseQueryOptions<GetCzechSuklRsAtcGroupsAutocompleteQueryReturnType, TError, TData>;
		request?: SecondParameter<typeof apiClientMutator>;
	}
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getCzechSuklRsAtcGroupsAutocompleteQueryKey(params);

	const queryFn: QueryFunction<GetCzechSuklRsAtcGroupsAutocompleteQueryReturnType> = () =>
		getCzechSuklRsAtcGroupsAutocomplete(params, requestOptions);

	const query = useQuery<GetCzechSuklRsAtcGroupsAutocompleteQueryReturnType, TError, TData>(queryKey, queryFn, {
		keepPreviousData: true,
		...queryOptions,
	}) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

	query.queryKey = queryKey;

	return query;
}

export interface CzechSuklRsAtcGroupsExportBody {
	ids: string[];
}

export function postCzechSuklRsAtcGroupsExport(
	body: CzechSuklRsAtcGroupsExportBody,
	options?: SecondParameter<typeof apiClientMutator>
) {
	return apiClientMutator<WrappedExportResponse>(
		{
			url: '/product-sukl-rs-atc-groups/export',
			method: 'post',
			data: JSON.stringify(body),
		},
		options
	);
}

export type PostCzechSuklRsAtcGroupsExportMutationResult = Awaited<ReturnType<typeof postCzechSuklRsAtcGroupsExport>>;

export type PostCzechSuklRsAtcGroupsExportMutationError = ErrorType<ErrorsResponse>;

export function useCzechSuklRsAtcGroupsExport<
	TError = PostCzechSuklRsAtcGroupsExportMutationError,
	TContext = unknown
>(options?: {
	mutation?: UseMutationOptions<
		PostCzechSuklRsAtcGroupsExportMutationResult,
		TError,
		CzechSuklRsAtcGroupsExportBody,
		TContext
	>;
	request?: SecondParameter<typeof apiClientMutator>;
}) {
	const { mutation: mutationOptions, request: requestOptions } = options ?? {};

	return useMutation<PostCzechSuklRsAtcGroupsExportMutationResult, TError, CzechSuklRsAtcGroupsExportBody, TContext>({
		mutationFn: (body) => postCzechSuklRsAtcGroupsExport(body, requestOptions),
		...mutationOptions,
	});
}

export function getCzechSuklRsAtcGroupsRs(options?: SecondParameter<typeof apiClientMutator>) {
	return apiClientMutator<ItemsResponse<string>>(
		{
			url: '/product-sukl-rs-atc-groups/rs',
			method: 'get',
		},
		options
	);
}

export function getCzechSuklRsAtcGroupsRsQueryKey() {
	return ['/product-sukl-rs-atc-groups/rs'] as const;
}

type GetCzechSuklRsAtcGroupsRsQueryReturnType = Awaited<ReturnType<typeof getCzechSuklRsAtcGroupsRs>>;

export type GetCzechSuklRsAtcGroupsRsQueryResult = NonNullable<GetCzechSuklRsAtcGroupsRsQueryReturnType>;

export type GetCzechSuklRsAtcGroupsRsQueryError = ErrorType<ErrorsResponse>;

export function useCzechSuklRsAtcGroupsRs<
	TData = GetCzechSuklRsAtcGroupsRsQueryReturnType,
	TError = GetCzechSuklRsAtcGroupsRsQueryError
>(options?: {
	query?: UseQueryOptions<GetCzechSuklRsAtcGroupsRsQueryReturnType, TError, TData>;
	request?: SecondParameter<typeof apiClientMutator>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
	const { query: queryOptions, request: requestOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getCzechSuklRsAtcGroupsRsQueryKey();

	const queryFn: QueryFunction<GetCzechSuklRsAtcGroupsRsQueryReturnType> = () =>
		getCzechSuklRsAtcGroupsRs(requestOptions);

	const query = useQuery<GetCzechSuklRsAtcGroupsRsQueryReturnType, TError, TData>(queryKey, queryFn, {
		staleTime: Infinity,
		...queryOptions,
	}) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

	query.queryKey = queryKey;

	return query;
}
